import { getApiErrorStatus } from '@/helpers/notify'

/**
 * Station writes that must outlive the grid that sent them.
 *
 * SCRUM-1032 review — on the dev controller a write takes 15–113 s, and a
 * second station on one decoder freezes both (the API can update neither).
 * The guards against sending a create twice used to live in the grid's refs,
 * so Back, Exit or a reload while a create was out threw them away: the step
 * came back, rebuilt the draft from local storage and created it again.
 *
 * Two things are kept here, per controller:
 *
 * - **Writes in flight** (`update:<rowId>`, `create:<address>`), in memory.
 *   A promise cannot survive a reload, but it does survive the step unmounting.
 * - **Create holds**, in memory and in sessionStorage: an address whose create
 *   was sent and whose outcome is not known for certain. Each carries a time;
 *   only a station-list read that *started* after it can say whether the
 *   station exists. Until then the address is not written.
 */

/**
 * How long after a create is sent the server may still be making it.
 *
 * The slowest write measured on the dev controller took 113 s, and a gateway
 * 502/504 answers long before the controller behind it has finished. A hold
 * from an unanswered or unclear create lasts at least this long from the send.
 */
export const CREATE_SETTLE_MS = 180_000

const STORAGE_PREFIX = 'cisgenics.stationCreateHolds.'

const holdsByInterface = new Map<string, Map<string, number>>()
const inFlightByInterface = new Map<string, Map<string, Promise<unknown>>>()
const lateListeners = new Map<string, Set<() => void>>()

const keyOf = (interfaceId: string | number | null | undefined) =>
  String(interfaceId ?? '')

const readStored = (key: string): Map<string, number> => {
  const holds = new Map<string, number>()
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key)
    if (!raw) return holds
    Object.entries(JSON.parse(raw) as Record<string, unknown>).forEach(
      ([address, until]) => {
        if (typeof until === 'number' && Number.isFinite(until))
          holds.set(address, until)
      },
    )
  } catch {
    // Unreadable or blocked storage: no holds survive a reload.
  }
  return holds
}

const holdsFor = (key: string) => {
  let holds = holdsByInterface.get(key)
  if (!holds) {
    holds = readStored(key)
    holdsByInterface.set(key, holds)
  }
  return holds
}

const persist = (key: string) => {
  const holds = holdsFor(key)
  try {
    if (holds.size === 0) window.sessionStorage.removeItem(STORAGE_PREFIX + key)
    else
      window.sessionStorage.setItem(
        STORAGE_PREFIX + key,
        JSON.stringify(Object.fromEntries(holds)),
      )
  } catch {
    // The in-memory hold still covers Back and Exit.
  }
}

/** The controller's writes still out, shared by every mount of the grid. */
export const stationWritesInFlight = (
  interfaceId: string | number | null | undefined,
) => {
  const key = keyOf(interfaceId)
  let inFlight = inFlightByInterface.get(key)
  if (!inFlight) {
    inFlight = new Map()
    inFlightByInterface.set(key, inFlight)
  }
  return inFlight
}

/**
 * A create is about to be sent. Held until `sentAt + CREATE_SETTLE_MS` so a
 * reload mid-request does not offer it again before a list read can tell.
 */
export const holdCreateSent = (
  interfaceId: string | number | null | undefined,
  address: string,
  sentAt: number,
) => {
  if (!address) return
  const key = keyOf(interfaceId)
  holdsFor(key).set(address, sentAt + CREATE_SETTLE_MS)
  persist(key)
}

/**
 * Whether a failed create may have been made anyway.
 *
 * A 4xx is the server saying no. Anything else — a 5xx (a gateway 502/504
 * answers while the controller is still working), a network error, this
 * client's own timeout — says nothing about what the server did.
 */
export const isUnclearCreateFailure = (error: unknown) => {
  const status = Number(getApiErrorStatus(error))
  if (!Number.isFinite(status) || status === 0) return true
  if (status === 408 || status === 429) return true
  return status >= 500
}

export type CreateOutcome = 'created' | 'refused' | 'unclear'

/**
 * A create answered. `created` holds until a list read after now shows the
 * station; `refused` releases it; `unclear` holds until a read after the
 * settle window.
 */
export const settleCreateHold = (
  interfaceId: string | number | null | undefined,
  address: string,
  outcome: CreateOutcome,
  sentAt: number,
  { late = false }: { late?: boolean } = {},
) => {
  if (!address) return
  const key = keyOf(interfaceId)
  const holds = holdsFor(key)
  const now = Date.now()
  if (outcome === 'refused') holds.delete(address)
  else if (outcome === 'created') holds.set(address, now)
  else holds.set(address, Math.max(now, sentAt + CREATE_SETTLE_MS))
  persist(key)
  // A late answer may arrive after the grid that sent it is gone; whichever
  // grid is showing this controller now re-reads the list.
  if (late) lateListeners.get(key)?.forEach((listener) => listener())
}

/** The time a list read must start after to decide this address, if held. */
export const createHoldUntil = (
  interfaceId: string | number | null | undefined,
  address: string,
): number | undefined => holdsFor(keyOf(interfaceId)).get(address)

export const releaseCreateHold = (
  interfaceId: string | number | null | undefined,
  address: string,
) => {
  const key = keyOf(interfaceId)
  if (holdsFor(key).delete(address)) persist(key)
}

export const hasCreateHolds = (
  interfaceId: string | number | null | undefined,
) => holdsFor(keyOf(interfaceId)).size > 0

/** Called when a create that outlived its pass answers. */
export const subscribeLateCreates = (
  interfaceId: string | number | null | undefined,
  listener: () => void,
) => {
  const key = keyOf(interfaceId)
  let listeners = lateListeners.get(key)
  if (!listeners) {
    listeners = new Set()
    lateListeners.set(key, listeners)
  }
  listeners.add(listener)
  return () => {
    listeners?.delete(listener)
  }
}

/** Tests only: what a reload does — memory gone, sessionStorage kept. */
export const dropInMemoryStationWrites = () => {
  holdsByInterface.clear()
  inFlightByInterface.clear()
}

/** Tests only: forget everything, memory and storage. */
export const resetStationWriteRegistry = () => {
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX))
      .forEach((key) => window.sessionStorage.removeItem(key))
  } catch {
    // ignore
  }
  holdsByInterface.clear()
  inFlightByInterface.clear()
  lateListeners.clear()
}
